import React from 'react';

interface AccountCost {
  account: string;
  cost: number;
  percentage: number;
}

interface CostByAccountProps {
  accounts: AccountCost[];
  currency?: string;
}

/**
 * Cost by Account Component
 * Displays cost breakdown by cloud accounts
 */
export const CostByAccount: React.FC<CostByAccountProps> = ({ accounts, currency = 'USD' }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (!accounts || accounts.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 dark:border-border dark:bg-card">
        <h3 className="text-lg font-semibold text-foreground">Cost by Account</h3>
        <p className="mt-4 text-sm text-muted-foreground">No data available</p>
      </div>
    );
  }

  // Sort by cost descending
  const sortedAccounts = [...accounts].sort((a, b) => b.cost - a.cost);
  const topAccounts = sortedAccounts.slice(0, 10); // Show top 10

  return (
    <div className="rounded-lg border border-border bg-card p-6 dark:border-border dark:bg-card">
      <h3 className="text-lg font-semibold text-foreground">Cost by Account</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Top {topAccounts.length} accounts by cost
      </p>

      <div className="mt-6 space-y-4">
        {topAccounts.map((account, index) => (
          <div key={account.account} className="flex items-center gap-4">
            {/* Rank */}
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-sky-100 text-sm font-semibold text-sky-700 dark:bg-sky-900 dark:text-sky-300">
              {index + 1}
            </div>

            {/* Account ID */}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-foreground">
                {account.account}
              </div>
              {/* Progress Bar */}
              <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-secondary">
                <div
                  className="h-full rounded-full bg-sky-600 transition-all duration-300"
                  style={{ width: `${account.percentage}%` }}
                />
              </div>
            </div>

            {/* Cost and Percentage */}
            <div className="flex flex-col items-end">
              <div className="text-sm font-semibold text-foreground">
                {formatCurrency(account.cost)}
              </div>
              <div className="text-xs text-muted-foreground">
                {formatPercentage(account.percentage)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {sortedAccounts.length > 10 && (
        <div className="mt-4 text-center text-sm text-muted-foreground">
          +{sortedAccounts.length - 10} more accounts
        </div>
      )}
    </div>
  );
};
